import { Fragment, type CSSProperties } from 'react'

/**
 * 브랜드 그라디언트. 디자인 규칙상 **Hero 제목의 강조 단어에만** 쓴다.
 * (핸드오프 문서 Design Tokens 참고)
 *
 * 끝색은 원래 `#5F12CC` 였는데 어두운 배경(#08090C)에서 대비가 2.32:1 밖에 안 나와
 * 마지막 글자들이 묻힌다는 피드백이 있었다. 큰 글씨 최소 기준(3:1)에도 못 미치는 값이다.
 * 보라색 느낌을 최대한 남기는 선으로 `#7C3AED` 를 골랐다 — 대비 3.49:1 로 큰 글씨 최소 기준은
 * 넘지만 WCAG AA(4.5:1)에는 못 미치므로, **큰 제목 외의 작은 글씨에는 쓰지 말 것.**
 * 색을 바꿀 때는 배경 대비를 먼저 확인할 것 — 앞쪽 두 색은 13.68:1 / 6.15:1 이다.
 */
export const BRAND_GRADIENT_STYLE: CSSProperties = {
  background: 'linear-gradient(120deg,#6AE7FA,#2E90FA 46%,#7C3AED)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
}

export interface HeadingPart {
  text: string
  highlighted: boolean
}

/**
 * 제목 전체 문장에서 강조할 부분을 찾아 세 조각으로 나눈다.
 *
 * - 강조 단어가 비어 있거나 문장에 없으면 전체를 일반 텍스트로 취급한다
 * - **처음 나오는 한 곳만** 강조한다. 같은 단어가 두 번 나올 때 둘 다 칠하면
 *   의도와 다른 결과가 나오기 쉬워서, 예측 가능한 쪽을 택했다.
 * - 대소문자와 공백을 그대로 비교한다 (문장에 있는 그대로 적어야 한다)
 */
export function splitHighlight(heading: string, highlight: string): HeadingPart[] {
  const needle = highlight.trim()
  const at = needle ? heading.indexOf(needle) : -1

  if (at < 0) return [{ text: heading, highlighted: false }]

  return [
    { text: heading.slice(0, at), highlighted: false },
    { text: heading.slice(at, at + needle.length), highlighted: true },
    { text: heading.slice(at + needle.length), highlighted: false },
  ].filter((p) => p.text !== '')
}

/** 줄바꿈(\n)을 <br>로 바꿔 렌더 */
function withLineBreaks(text: string) {
  const lines = text.split('\n')
  return lines.map((line, i) => (
    <Fragment key={i}>
      {line}
      {i < lines.length - 1 && <br />}
    </Fragment>
  ))
}

/**
 * 강조 단어에만 브랜드 그라디언트를 입힌 제목.
 *
 * Hero 섹션과 Admin 미리보기가 **같은 컴포넌트**를 쓴다.
 * 그래야 Admin에서 본 모습과 실제 화면이 어긋나지 않는다.
 */
export function GradientHeading({
  heading,
  highlight,
}: {
  heading: string
  highlight: string
}) {
  return (
    <>
      {splitHighlight(heading, highlight).map((part, i) =>
        part.highlighted ? (
          <span key={i} style={BRAND_GRADIENT_STYLE}>
            {withLineBreaks(part.text)}
          </span>
        ) : (
          <Fragment key={i}>{withLineBreaks(part.text)}</Fragment>
        ),
      )}
    </>
  )
}
